// Suara TAKA:
//   - TTS: suara natural dari server (/api/suara: Azure atau Google, lihat lib/suara.js).
//          Kalau server tidak aktif, kuota habis, offline, atau versi demo, otomatis memakai
//          speechSynthesis bawaan browser (suara bahasa Indonesia bila tersedia di perangkat).
//   - STT: SpeechRecognition (Chrome/Edge/Safari); diproses oleh layanan Google/Apple,
//          aktif hanya selama tombol mikrofon ditekan, tidak ada rekaman yang disimpan.
import { teksUntukSuara, potongKalimat } from '../core/ucapan.js';
import { store } from '../core/storage.js';

const DEMO = typeof __DEMO__ !== 'undefined' && __DEMO__;
const SR = typeof window !== 'undefined' ? (window.SpeechRecognition || window.webkitSpeechRecognition) : null;
const suaraBrowser = typeof window !== 'undefined' && 'speechSynthesis' in window && 'SpeechSynthesisUtterance' in window;
const adaAudio = typeof window !== 'undefined' && typeof Audio !== 'undefined';
export const dukungTTS = suaraBrowser || (adaAudio && !DEMO);
export const dukungSTT = !!SR;

const KUNCI = 'tka-taka-suara';
export const suaraTakaNyala = () => dukungTTS && store.get(KUNCI, true);
export const aturSuaraTaka = v => store.set(KUNCI, !!v);

// ---------- TTS bawaan browser (cadangan) ----------
let suaraId = null;
function pilihSuara() {
  if (!suaraBrowser) return null;
  const semua = speechSynthesis.getVoices().filter(v => /^id([-_]|$)/i.test(v.lang));
  // Utamakan suara jaringan/neural yang biasanya lebih natural.
  suaraId = semua.find(v => /google|natural|online|neural/i.test(v.name)) || semua.find(v => /gadis|damayanti|andika/i.test(v.name)) || semua[0] || null;
  return suaraId;
}
if (suaraBrowser) { pilihSuara(); speechSynthesis.addEventListener?.('voiceschanged', pilihSuara); }
export const adaSuaraIndonesia = () => !!(suaraId || pilihSuara());

function bicaraBrowser(bagian, ini, onMulai, onSelesai) {
  if (!suaraBrowser || !bagian.length) { onSelesai?.(); return; }
  onMulai?.();
  bagian.forEach((b, i) => {
    const u = new SpeechSynthesisUtterance(b);
    u.lang = 'id-ID';
    if (suaraId) u.voice = suaraId;
    u.rate = 0.95; u.pitch = 1.1;
    if (i === bagian.length - 1) u.onend = u.onerror = () => { if (ini === giliran) onSelesai?.(); };
    speechSynthesis.speak(u);
  });
}

// ---------- suara natural dari server ----------
const KUNCI_JEDA = 'tka-suara-server-jeda';
const JEDA_GAGAL = 10 * 60e3;    // kuota habis/gangguan: pakai suara browser dulu selama 10 menit
const JEDA_MATI = 6 * 3600e3;    // server belum mengaktifkan suara natural
const serverBoleh = () => adaAudio && !DEMO && navigator.onLine !== false && Date.now() > (Number(store.get(KUNCI_JEDA, 0)) || 0);
let audio = null;
const elAudio = () => audio || (audio = Object.assign(new Audio(), { preload: 'auto' }));

// WAV sunyi 50 ms untuk "membuka" pemutaran audio pada sentuhan pertama (iOS Safari).
function wavSunyi() {
  const n = 400, b = new DataView(new ArrayBuffer(44 + n));
  const tulis = (o, t) => [...t].forEach((c, i) => b.setUint8(o + i, c.charCodeAt(0)));
  tulis(0, 'RIFF'); b.setUint32(4, 36 + n, true); tulis(8, 'WAVEfmt '); b.setUint32(16, 16, true); b.setUint16(20, 1, true); b.setUint16(22, 1, true);
  b.setUint32(24, 8000, true); b.setUint32(28, 8000, true); b.setUint16(32, 1, true); b.setUint16(34, 8, true); tulis(36, 'data'); b.setUint32(40, n, true);
  for (let i = 0; i < n; i++) b.setUint8(44 + i, 128);
  return 'data:audio/wav;base64,' + btoa(String.fromCharCode(...new Uint8Array(b.buffer)));
}
if (adaAudio && !DEMO) {
  addEventListener('pointerdown', () => {
    const a = elAudio();
    if (a.src) return;
    a.src = wavSunyi();
    a.play().then(() => a.pause()).catch(() => {});
  }, { once: true, capture: true });
}

// Gabungkan kalimat pendek menjadi potongan <= 550 karakter (satu permintaan per potongan).
// Kalimat yang lebih panjang dari itu dipecah per kata (server menolak > 600 karakter).
function gabung(bagian, maks = 550) {
  const out = [];
  const pecah = b => {
    if (b.length <= maks) return [b];
    const hasil = [''];
    for (const kata of b.split(' ')) {
      const t = hasil.at(-1) ? hasil.at(-1) + ' ' + kata : kata;
      if (t.length <= maks || !hasil.at(-1)) hasil[hasil.length - 1] = t.slice(0, maks); else hasil.push(kata.slice(0, maks));
    }
    return hasil;
  };
  for (const b of bagian.flatMap(pecah)) {
    if (out.length && (out.at(-1) + ' ' + b).length <= maks) out[out.length - 1] += ' ' + b; else out.push(b);
  }
  return out;
}

// Ambil satu potongan audio. Mengembalikan URL blob, atau { alasan } bila gagal.
async function ambilAudio(teks) {
  try {
    const r = await fetch('/api/suara?t=' + encodeURIComponent(teks), { credentials: 'same-origin' });
    if (r.ok) return URL.createObjectURL(await r.blob());
    let alasan = '';
    try { alasan = (await r.json()).alasan || ''; } catch (e) { /* bukan JSON */ }
    return { alasan };
  } catch (e) {
    return { alasan: 'jaringan' };
  }
}

function putar(src, ini, onMulai) {
  return new Promise(selesai => {
    const a = elAudio();
    a.onplaying = () => { if (ini === giliran) onMulai?.(); };
    a.onended = () => selesai(true);
    a.onerror = () => selesai(false);
    a.src = src;
    a.play().catch(() => selesai(false));
  });
}

let giliran = 0;
/** Bacakan teks. onMulai/onSelesai dipanggil untuk animasi maskot. */
export async function bicara(teks, { onMulai, onSelesai } = {}) {
  if (!dukungTTS) return;
  berhentiBicara();
  const ini = ++giliran;
  const kalimat = potongKalimat(teksUntukSuara(teks));
  if (!kalimat.length) return;
  if (!serverBoleh()) { bicaraBrowser(kalimat, ini, onMulai, onSelesai); return; }

  const potongan = gabung(kalimat);
  let sudahMulai = false;
  const mulai = () => { if (!sudahMulai) { sudahMulai = true; onMulai?.(); } };
  let berikut = ambilAudio(potongan[0]);
  for (let k = 0; k < potongan.length; k++) {
    const src = await berikut;
    if (ini !== giliran) { if (typeof src === 'string') URL.revokeObjectURL(src); return; }
    // Potongan berikutnya diambil sambil potongan ini diputar, supaya jedanya tidak terasa.
    berikut = k + 1 < potongan.length ? ambilAudio(potongan[k + 1]) : null;
    const ok = typeof src === 'string' && await putar(src, ini, mulai);
    if (typeof src === 'string') URL.revokeObjectURL(src);
    if (ini !== giliran) return;
    if (!ok) {
      // Belum diaktifkan: jangan coba lagi selama 6 jam. Kuota habis/gangguan: 10 menit.
      if (typeof src === 'string' || src.alasan !== 'jaringan') store.set(KUNCI_JEDA, Date.now() + (src.alasan === 'mati' ? JEDA_MATI : JEDA_GAGAL));
      berikut?.then(u => { if (typeof u === 'string') URL.revokeObjectURL(u); });
      // Lanjutkan sisanya dengan suara browser.
      bicaraBrowser(potongKalimat(potongan.slice(k).join(' ')), ini, sudahMulai ? null : onMulai, onSelesai);
      return;
    }
  }
  onSelesai?.();
}
export function berhentiBicara() {
  giliran++;
  if (audio) { audio.onended = audio.onerror = audio.onplaying = null; audio.pause(); }
  if (suaraBrowser) speechSynthesis.cancel();
}

// ---------- STT (tekan-tahan) ----------
const PESAN_GALAT = {
  'not-allowed': 'Mikrofon belum diizinkan. Izinkan mikrofon di pengaturan browser, lalu coba lagi.',
  'service-not-allowed': 'Mikrofon belum diizinkan di perangkat ini.',
  'audio-capture': 'Mikrofon tidak ditemukan di perangkat ini.',
  'network': 'Mendengarkan suara perlu internet. Periksa koneksimu.',
  'no-speech': 'TAKA tidak mendengar suaramu. Tahan tombolnya sambil bicara, ya.',
  'language-not-supported': 'Perangkat ini belum mendukung pengenalan suara bahasa Indonesia.',
};

/**
 * Mulai mendengarkan. Kembalikan fungsi berhenti.
 * @param {{onTeks: (teks: string, final: boolean) => void, onSelesai: (teks: string) => void, onGalat: (pesan: string) => void}} cb
 */
export function dengarkan({ onTeks, onSelesai, onGalat }) {
  if (!SR) { onGalat?.('Perangkat ini belum mendukung bicara ke TAKA. Ketik pertanyaanmu, ya.'); return () => {}; }
  const rec = new SR();
  rec.lang = 'id-ID';
  rec.interimResults = true;
  rec.continuous = true;
  rec.maxAlternatives = 1;
  let akhir = '', sementara = '', selesai = false;
  rec.onresult = e => {
    sementara = '';
    for (let i = e.resultIndex; i < e.results.length; i++) {
      const r = e.results[i];
      if (r.isFinal) akhir += ' ' + r[0].transcript; else sementara += ' ' + r[0].transcript;
    }
    onTeks?.((akhir + ' ' + sementara).replace(/\s+/g, ' ').trim(), false);
  };
  rec.onerror = e => { if (e.error !== 'aborted') onGalat?.(PESAN_GALAT[e.error] || 'TAKA belum bisa mendengar. Coba lagi, ya.'); };
  rec.onend = () => { if (selesai) return; selesai = true; onSelesai?.((akhir + ' ' + sementara).replace(/\s+/g, ' ').trim()); };
  try { rec.start(); } catch (e) { onGalat?.('TAKA belum bisa mendengar. Coba lagi, ya.'); }
  let dihentikan = false;
  return () => { if (dihentikan) return; dihentikan = true; try { rec.stop(); } catch (e) { /* sudah berhenti */ } };
}
